import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { adminVisitorTrackingService, adminMessagingService } from '../../../services/adminUserManagementService';
import { playerProfileService } from '../../../services/playerMasterService';
import { supabase } from '../../../lib/supabase';

const AdminVisitorManager = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState('visitors');
  const [loading, setLoading] = useState(false);
  const [visitors, setVisitors] = useState([]);
  const [messages, setMessages] = useState([]);
  const [players, setPlayers] = useState([]);
  const [users, setUsers] = useState([]);
  const [showVisitorForm, setShowVisitorForm] = useState(false);
  const [showMessageForm, setShowMessageForm] = useState(false);
  const [editingVisitor, setEditingVisitor] = useState(null);
  const [selectedPlayerId, setSelectedPlayerId] = useState('');

  const [visitorForm, setVisitorForm] = useState({
    playerId: '',
    visitorName: '',
    visitorPosition: '',
    visitorClub: '',
    visitorCountry: '',
    visitorLeague: '',
    visitorRegion: '',
    visitDate: new Date()?.toISOString()?.split('T')?.[0],
    notes: ''
  });

  const [messageForm, setMessageForm] = useState({
    toUserId: '',
    toPlayerId: '',
    subject: '',
    message: ''
  });

  useEffect(() => {
    loadData();
  }, [activeTab, selectedPlayerId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const playersRes = await playerProfileService?.getAll();
      setPlayers(playersRes?.data || []);

      if (activeTab === 'visitors') {
        if (selectedPlayerId) {
          const visitorsRes = await adminVisitorTrackingService?.getVisitorsByPlayer(selectedPlayerId);
          setVisitors(visitorsRes?.data || []);
        } else {
          setVisitors([]);
        }
      } else if (activeTab === 'messages') {
        const { data: usersData } = await supabase
          ?.from('user_profiles')
          ?.select('id, full_name, email, role')
          ?.order('full_name', { ascending: true });
        setUsers(usersData || []);

        const { data: messagesData } = await supabase
          ?.from('admin_messages')
          ?.select('*, to_user:user_profiles!admin_messages_to_user_id_fkey(full_name, email), to_player:player_master_profiles(full_name)')
          ?.order('created_at', { ascending: false })
          ?.limit(50);
        setMessages(messagesData || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVisitor = async () => {
    if (!visitorForm?.playerId || !visitorForm?.visitorName) {
      alert('Please select a player and enter visitor name');
      return;
    }

    try {
      await adminVisitorTrackingService?.addVisitor(visitorForm);
      setShowVisitorForm(false);
      resetVisitorForm();
      loadData();
    } catch (error) {
      console.error('Error creating visitor:', error);
      alert('Failed to add visitor: ' + error?.message);
    }
  };

  const handleUpdateVisitor = async () => {
    try {
      await adminVisitorTrackingService?.updateVisitor(editingVisitor?.id, visitorForm);
      setEditingVisitor(null);
      resetVisitorForm();
      loadData();
    } catch (error) {
      console.error('Error updating visitor:', error);
      alert('Failed to update visitor: ' + error?.message);
    }
  };

  const handleDeleteVisitor = async (id) => {
    if (!window.confirm('Are you sure you want to delete this visitor record?')) return;

    try {
      await adminVisitorTrackingService?.deleteVisitor(id);
      loadData();
    } catch (error) {
      console.error('Error deleting visitor:', error);
      alert('Failed to delete visitor: ' + error?.message);
    }
  };

  const handleSendMessage = async () => {
    if (!messageForm?.toUserId || !messageForm?.subject || !messageForm?.message) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      await adminMessagingService?.sendMessage(messageForm);
      setShowMessageForm(false);
      resetMessageForm();
      loadData();
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message: ' + error?.message);
    }
  };

  const resetVisitorForm = () => {
    setVisitorForm({
      playerId: '',
      visitorName: '',
      visitorPosition: '',
      visitorClub: '',
      visitorCountry: '',
      visitorLeague: '',
      visitorRegion: '',
      visitDate: new Date()?.toISOString()?.split('T')?.[0],
      notes: ''
    });
  };

  const resetMessageForm = () => {
    setMessageForm({
      toUserId: '',
      toPlayerId: '',
      subject: '',
      message: ''
    });
  };

  const handleEditVisitor = (visitor) => {
    setEditingVisitor(visitor);
    setVisitorForm({
      playerId: visitor?.playerId,
      visitorName: visitor?.visitorName,
      visitorPosition: visitor?.visitorPosition || '',
      visitorClub: visitor?.visitorClub || '',
      visitorCountry: visitor?.visitorCountry || '',
      visitorLeague: visitor?.visitorLeague || '',
      visitorRegion: visitor?.visitorRegion || '',
      visitDate: visitor?.visitDate?.split('T')?.[0] || new Date()?.toISOString()?.split('T')?.[0],
      notes: visitor?.notes || ''
    });
    setShowVisitorForm(true);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg border border-border w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-2xl font-bold text-foreground">Admin Visitor & Messaging Manager</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border">
          <button
            onClick={() => setActiveTab('visitors')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'visitors' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="Eye" size={18} className="inline mr-2" />
            Visitor Tracking
          </button>
          <button
            onClick={() => setActiveTab('messages')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'messages' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="Mail" size={18} className="inline mr-2" />
            Admin Messages
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'visitors' && (
            <div className="space-y-6">
              {/* Player Selection */}
              <div className="bg-muted/50 rounded-lg p-4">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Select Player to View/Add Visitors
                </label>
                <Select
                  value={selectedPlayerId}
                  onChange={(e) => setSelectedPlayerId(e?.target?.value)}
                  options={[
                    { value: '', label: 'Select a player...' },
                    ...players?.map(p => ({
                      value: p?.id,
                      label: `${p?.fullName || p?.full_name} - ${p?.primaryPosition || p?.primary_position || 'N/A'}`
                    }))
                  ]}
                />
              </div>

              {selectedPlayerId && (
                <>
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-foreground">
                      Visitor Records ({visitors?.length})
                    </h3>
                    <Button
                      onClick={() => {
                        setEditingVisitor(null);
                        setVisitorForm({ ...visitorForm, playerId: selectedPlayerId });
                        setShowVisitorForm(true);
                      }}
                      iconName="Plus"
                    >
                      Add Visitor
                    </Button>
                  </div>

                  {/* Visitors List */}
                  <div className="space-y-3">
                    {visitors?.length === 0 ? (
                      <div className="text-center py-12 text-muted-foreground">
                        <Icon name="Users" size={48} className="mx-auto mb-4 opacity-50" />
                        <p>No visitor records yet</p>
                      </div>
                    ) : (
                      visitors?.map((visitor) => (
                        <div key={visitor?.id} className="bg-card border border-border rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-semibold text-foreground mb-2">{visitor?.visitorName}</h4>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                                {visitor?.visitorPosition && (
                                  <div>
                                    <span className="text-muted-foreground">Position:</span>
                                    <span className="text-foreground ml-2">{visitor?.visitorPosition}</span>
                                  </div>
                                )}
                                {visitor?.visitorClub && (
                                  <div>
                                    <span className="text-muted-foreground">Club:</span>
                                    <span className="text-foreground ml-2">{visitor?.visitorClub}</span>
                                  </div>
                                )}
                                {visitor?.visitorCountry && (
                                  <div>
                                    <span className="text-muted-foreground">Country:</span>
                                    <span className="text-foreground ml-2">{visitor?.visitorCountry}</span>
                                  </div>
                                )}
                                {visitor?.visitorLeague && (
                                  <div>
                                    <span className="text-muted-foreground">League:</span>
                                    <span className="text-foreground ml-2">{visitor?.visitorLeague}</span>
                                  </div>
                                )}
                                {visitor?.visitorRegion && (
                                  <div>
                                    <span className="text-muted-foreground">Region:</span>
                                    <span className="text-foreground ml-2">{visitor?.visitorRegion}</span>
                                  </div>
                                )}
                                <div>
                                  <span className="text-muted-foreground">Visit Date:</span>
                                  <span className="text-foreground ml-2">
                                    {new Date(visitor?.visitDate)?.toLocaleDateString()}
                                  </span>
                                </div>
                              </div>
                              {visitor?.notes && (
                                <p className="text-sm text-muted-foreground mt-2">{visitor?.notes}</p>
                              )}
                            </div>
                            <div className="flex gap-2 ml-4">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditVisitor(visitor)}
                                iconName="Edit"
                                iconSize={14}
                              />
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDeleteVisitor(visitor?.id)}
                                iconName="Trash2"
                                iconSize={14}
                              />
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'messages' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-foreground">
                  Admin Messages ({messages?.length})
                </h3>
                <Button
                  onClick={() => setShowMessageForm(true)}
                  iconName="Plus"
                >
                  Send Message
                </Button>
              </div>

              {/* Messages List */}
              <div className="space-y-3">
                {messages?.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Icon name="Mail" size={48} className="mx-auto mb-4 opacity-50" />
                    <p>No messages sent yet</p>
                  </div>
                ) : (
                  messages?.map((message) => (
                    <div key={message?.id} className="bg-card border border-border rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h4 className="font-semibold text-foreground">{message?.subject}</h4>
                          <p className="text-sm text-muted-foreground">
                            To: {message?.to_user?.full_name} ({message?.to_user?.email})
                          </p>
                          {message?.to_player && (
                            <p className="text-sm text-muted-foreground">
                              Player: {message?.to_player?.full_name}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {message?.is_read ? (
                            <span className="px-2 py-1 bg-success/10 text-success text-xs rounded-full">
                              Read
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-warning/10 text-warning text-xs rounded-full">
                              Unread
                            </span>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-foreground mb-2">{message?.message}</p>
                      <p className="text-xs text-muted-foreground">
                        Sent: {new Date(message?.created_at)?.toLocaleString()}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Visitor Form Modal */}
      {showVisitorForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-card rounded-lg border border-border p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-foreground mb-4">
              {editingVisitor ? 'Edit Visitor Record' : 'Add Visitor Record'}
            </h3>
            <div className="space-y-4">
              <Input
                label="Visitor Name *"
                value={visitorForm?.visitorName}
                onChange={(e) => setVisitorForm({ ...visitorForm, visitorName: e?.target?.value })}
                placeholder="Enter visitor name"
              />
              <Input
                label="Position"
                value={visitorForm?.visitorPosition}
                onChange={(e) => setVisitorForm({ ...visitorForm, visitorPosition: e?.target?.value })}
                placeholder="E.g., Scout, Director, Coach"
              />
              <Input
                label="Club/Organization"
                value={visitorForm?.visitorClub}
                onChange={(e) => setVisitorForm({ ...visitorForm, visitorClub: e?.target?.value })}
                placeholder="Enter club or organization name"
              />
              <Input
                label="Country"
                value={visitorForm?.visitorCountry}
                onChange={(e) => setVisitorForm({ ...visitorForm, visitorCountry: e?.target?.value })}
                placeholder="Enter country"
              />
              <Input
                label="League"
                value={visitorForm?.visitorLeague}
                onChange={(e) => setVisitorForm({ ...visitorForm, visitorLeague: e?.target?.value })}
                placeholder="E.g., Premier League, La Liga"
              />
              <Input
                label="Region"
                value={visitorForm?.visitorRegion}
                onChange={(e) => setVisitorForm({ ...visitorForm, visitorRegion: e?.target?.value })}
                placeholder="E.g., Europe, Africa, Asia"
              />
              <Input
                type="date"
                label="Visit Date"
                value={visitorForm?.visitDate}
                onChange={(e) => setVisitorForm({ ...visitorForm, visitDate: e?.target?.value })}
              />
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Notes</label>
                <textarea
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  rows={3}
                  placeholder="Additional notes about the visit..."
                  value={visitorForm?.notes}
                  onChange={(e) => setVisitorForm({ ...visitorForm, notes: e?.target?.value })}
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setShowVisitorForm(false);
                  setEditingVisitor(null);
                  resetVisitorForm();
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={editingVisitor ? handleUpdateVisitor : handleCreateVisitor}
                iconName="Save"
                className="flex-1"
              >
                {editingVisitor ? 'Update' : 'Add'} Visitor
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Message Form Modal */}
      {showMessageForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-card rounded-lg border border-border p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-foreground mb-4">Send Admin Message</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">To User *</label>
                <Select
                  value={messageForm?.toUserId}
                  onChange={(e) => setMessageForm({ ...messageForm, toUserId: e?.target?.value })}
                  options={[
                    { value: '', label: 'Select a user...' },
                    ...users?.map(u => ({
                      value: u?.id,
                      label: `${u?.full_name} (${u?.role}) - ${u?.email}`
                    }))
                  ]}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Related Player (Optional)</label>
                <Select
                  value={messageForm?.toPlayerId}
                  onChange={(e) => setMessageForm({ ...messageForm, toPlayerId: e?.target?.value })}
                  options={[
                    { value: '', label: 'None' },
                    ...players?.map(p => ({
                      value: p?.id,
                      label: `${p?.fullName || p?.full_name} - ${p?.primaryPosition || p?.primary_position || 'N/A'}`
                    }))
                  ]}
                />
              </div>
              <Input
                label="Subject *"
                value={messageForm?.subject}
                onChange={(e) => setMessageForm({ ...messageForm, subject: e?.target?.value })}
                placeholder="Enter message subject"
              />
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Message *</label>
                <textarea
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  rows={6}
                  placeholder="Enter your message..."
                  value={messageForm?.message}
                  onChange={(e) => setMessageForm({ ...messageForm, message: e?.target?.value })}
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setShowMessageForm(false);
                  resetMessageForm();
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendMessage}
                iconName="Send"
                className="flex-1"
              >
                Send Message
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVisitorManager;